import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service: '',
    message: '',
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Enhanced XSS prevention - remove potential script tags and dangerous content
    let sanitizedValue = value;
    if (typeof value === 'string') {
      // Remove script tags and event handlers
      sanitizedValue = value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
        .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
        .replace(/javascript:/gi, '')
        .replace(/vbscript:/gi, '')
        .replace(/data:text\/html/gi, '');
    }
    
    setFormData({
      ...formData,
      [name]: sanitizedValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    // Client-side validation
    if (formData.name.length < 2 || formData.name.length > 100) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    if (formData.message.length < 10 || formData.message.length > 2000) {
      setSubmitStatus('error');
      setSubmitting(false);
      return;
    }

    try {
      await axios.post(`${API}/contact`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000, // 10 second timeout
      });
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        service: '',
        message: '',
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      
      if (error.response?.status === 429) {
        // Rate limit exceeded
        setSubmitStatus('rate_limit');
      } else {
        setSubmitStatus('error');
      }
      
      // Reset error message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>{i18n.language === 'tr' ? 'İletişim | SULH Siber Güvenlik' : 'Contact | SULH Cybersecurity'}</title>
        <meta name="description" content={i18n.language === 'tr' ? 'SULH Siber Güvenlik ile iletişime geçin. Penetrasyon testi, SOC ve siber güvenlik hizmetleri için bize ulaşın.' : 'Contact SULH Cybersecurity for penetration testing, SOC, and security services.'} />
        <link rel="canonical" href="https://www.sulh.tech/contact" />
      </Helmet>
      
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-blue-900/20 via-transparent to-cyan-900/20">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('contact.title')}
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Contact Information
              </h2>
              <p className="text-gray-300 mb-8">
                Reach out to our security experts. We're here to help you protect your digital assets.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-400/10 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <p className="text-gray-400">info@sulh.tech</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-400/10 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Phone</h3>
                    <p className="text-gray-400">+90 (232) 343-44 00</p>
                    <p className="text-gray-400 text-sm">24/7 Emergency Support</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-400/10 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Headquarter</h3>
                    <p className="text-gray-400">
                    Erzene Mahallesi, Ankara Cad<br />
                      Ebiltem Binası No:172/14<br />
                      35040 Bornova<br />
                      TÜRKİYE
                    </p>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="mt-8 glass-card p-6">
                <h3 className="text-white font-semibold mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-300">
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                  <p className="text-blue-400 text-sm mt-4">Emergency support available 24/7</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="glass-card p-8">
              <h2 className="text-2xl font-bold text-white mb-6">
                Send Us a Message
              </h2>
              
              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-green-400 text-sm">
                    Thank you for your message! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}
              
              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/50 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <p className="text-red-400 text-sm">
                    Failed to submit the form. Please try again or contact us directly via email.
                  </p>
                </div>
              )}
              
              {/* Rate Limit Message */}
              {submitStatus === 'rate_limit' && (
                <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/50 rounded-lg flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                  <p className="text-yellow-400 text-sm">
                    Too many submission attempts. Please wait a moment before trying again.
                  </p>
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
                    placeholder="John Doe"
                   
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
                    placeholder="john@company.com"
                   
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-400"
                    placeholder="Your Company"
                   
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                    Service Interest *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-400"
                   
                  >
                    <option value="">Select a service</option>
                    <option value="penetration-testing">Penetration Testing</option>
                    <option value="siem">SIEM Solutions</option>
                    <option value="ids-ips">IDS/IPS</option>
                    <option value="vulnerability-assessment">Vulnerability Assessment</option>
                    <option value="security-training">Security Awareness Training</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-400 resize-none"
                    placeholder="Tell us about your security needs..."
                   
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                 
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="section-padding bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 text-center">
            Our Location
          </h2>
          <div className="glass-card p-4">
            <div className="rounded-lg overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3125.123456789!2d27.123456!3d38.456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDI3JzI0LjQiTiAyN8KwMDcnMjQuNCJF!5e0!3m2!1sen!2str!4v1234567890123!5m2!1sen!2str&q=Erzene+Mahallesi%2C+Ankara+Cad%2C+Ebiltem+Binası+No%3A172%2F14%2C+35040+Bornova%2C+İzmir%2C+Turkey"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SULH Cybersecurity Location - Erzene Mahallesi, Ankara Cad, Ebiltem Binası No:172/14, 35040 Bornova"
              ></iframe>
            </div>
            <div className="mt-4 text-center">
              <p className="text-gray-300 text-sm">
                <MapPin className="w-4 h-4 inline mr-2" />
                Erzene Mahallesi, Ankara Cad, Ebiltem Binası No:172/14, 35040 Bornova, İzmir, Türkiye
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
